import React, { Component } from 'react';
import { Fade } from 'reactstrap';
import { connect } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';
import {
  fetchConferenceRequest,
  endConferenceRequest,
  fetchMemberDataRequest,
  joinConferenceRequest,
  hangupUserConferenceRequest,
  startConferenceRequest,
  hangupCallRequest,
  execCallRequest,
  addConferenceMemberWebrtcRequest,
  recordCallRequest,
  stopRecordCallRequest,
  saveConferenceRequest,
} from '../../../js/phone/actions';
import {
  getConferenceUsers,
  getMeConferenceId,
  getMeActiveCallId,
  hasGrants,
  hasRecordedCurrentCall,
  getMeId,
} from '../../../js/me/selectors';
import Button from '../../../components/formControls/Button';
import Icon from '../../../components/icons/Icon';
import InterlocutorPanel from '../../../components/InterlocutorPanel';
import PhoneNumberSearchInput from './PhoneNumberSearchInput';
import { getSearchedNumber, isRecordCallAvailable, isRecording, isWebrtcActive } from '../../../js/phone/selectors';
import PhoneNumberSearchList from '../../../components/PhoneNumberSearchList';
import Utils from '../../../js/lib/utils';
import { showConfirmModal } from '../../../js/confirm/actions';

const messages = defineMessages({
  header: {
    id: 'ConferencePanel.header',
    defaultMessage: 'On going conference',
  },
  body: {
    id: 'ConferencePanel.body',
    defaultMessage: 'Conference',
  },
  addMember: {
    id: 'ConferencePanel.tooltip.addMember',
    defaultMessage: 'Add member',
  },
  addMemberMessage: {
    id: 'ConferencePanel.addMemberMessage',
    defaultMessage: 'If you add a member, the recording will stop and you will have to start it again',
  },
  exitConferenceMessage: {
    id: 'ConferencePanel.exitConferenceMessage',
    defaultMessage: 'If you exit the conference, the recording will stop and you will have to start it again',
  },
  startRec: {
    id: 'ConferencePanel.tooltip.startRec',
    defaultMessage: 'Start rec',
  },
  stopRecording: {
    id: 'ConferencePanel.tooltip.stopRecording',
    defaultMessage: 'Stop rec',
  },
  stopRecordingMessage: {
    id: 'ConferencePanel.stopRecordingMessage',
    defaultMessage:
      'Are you sure to stop recording? You cannot record again this conversation',
  },
  closeConferenceButton: {
    id: 'ConferencePanel.tooltip.closeConferenceButton',
    defaultMessage: 'Close conference',
  },
});

class ConferencePanel extends Component {
  state = {
    addMemberPanelOpen: false,
  };

  componentDidMount() {
    this.props.users.map((user) => this.props.fetchMember(user.number));
    const owner = this.props.users.find((member) => member.owner);
    if (owner.id === this.props.meId && owner.in) {
      this.props.saveConference({
        conferenceId: this.props.callId,
        members: this.props.users.map((member) => ({
          type: member.interlocutorType,
          id: member.id,
          number: member.number,
        })),
      });
    }
  }

  componentDidUpdate(prevprops) {
    if (prevprops.users.length < this.props.users.length) {
      this.props.users.forEach((user) => {
        if (!user.fetched) {
          this.props.fetchMember(user.number);
        }
      });
    }
    const oldOwner = prevprops.users.find((member) => member.owner);
    const currentOwner = this.props.users.find((member) => member.owner);
    if (currentOwner && currentOwner.in && currentOwner.id === this.props.meId && (this.props.callId !== prevprops.callId || (!oldOwner || !oldOwner.in))) {
      this.props.saveConference({
        conferenceId: this.props.callId,
        members: this.props.users.map((member) => ({
          type: member.interlocutorType,
          id: member.id,
          number: member.number,
        })),
      });
    }
  }

  handleClose = () => {
    this.props.users.length > 0
      ? this.props.hangupConference(this.props.id)
      : this.props.hangupCall(this.props.callId);
  };

  handleJoin = () => {
    this.props.join();
  };

  handleAddMemberClick = () => {
    if (this.props.recording) {
      this.props.confirm({
        message: this.props.intl.formatMessage(messages.addMemberMessage),
        modalAction: () => this.setState({ addMemberPanelOpen: true, })
      });
    } else {
      this.setState({ addMemberPanelOpen: true, });
    }
  };

  handleAddMember = (number) => {
    if (this.props.webrtcActive === 'ON') {
      const numberToCall = Utils.adjustPhoneNumber(number);
      if (Utils.validatePhoneNumber(numberToCall)) {
        this.props.addMemberWebrtc({
          number: numberToCall,
          conferenceId: this.props.id,
        });
      }
    } else {
      this.props.addMember({
        callId: this.props.callId,
        number,
      });
    }
    this.setState({ addMemberPanelOpen: false, });
  };

  handleHangupUser = (user) => {
    if (this.props.recording && user.interlocutorType === 'U' && user.id === this.props.meId) {
      this.props.confirm({
        message: this.props.intl.formatMessage(messages.exitConferenceMessage),
        modalAction: () => this.props.hangupUser({ number: user.number, conferenceId: this.props.id }),
      });
    } else {
      this.props.hangupUser({ number: user.number, conferenceId: this.props.id });
    }
  };

  handleRec = () => {
    this.props.recordCall({
      callId: this.props.callId,
      conference: true,
      users: this.props.users.filter((member => member.interlocutorType === 'U')).map((member) => member.id),
    });
  };

  handleStopRec = () => {
    this.props.confirm({
      message: this.props.intl.formatMessage(messages.stopRecordingMessage),
      modalAction: this.props.stopRecordCall,
      actionParams: {
        callId: this.props.callId,
        users: this.props.users.filter((member => member.interlocutorType === 'U')).map((member) => member.id),
      },
    });
  };

  render() {
    const {
      users,
      id,
      number,
      activeCall,
      isRecordCallAvailable,
      canRecord,
      hasRecordedCurrentCall,
      recording,
      intl: { formatMessage },
    } = this.props;
    const ownerIn =
      users.filter((member) => member.owner && member.in).length === 1;
    const record = isRecordCallAvailable && canRecord && !recording && !hasRecordedCurrentCall;
    return (
      <Fade id="ConferencePanel" className="card mt-2">
        <div className="card-header text-left small p-1">
          <div className="row">
            <div className="col-12">{formatMessage(messages.header)}</div>
          </div>
        </div>
        {users.length > 0 &&
          users.map(
            (user) =>
              user.number &&
              !(activeCall && user.owner) && (
                <div
                  className="card-body p-1 text-left small border-bottom"
                  key={user.number}
                >
                  <InterlocutorPanel
                    interlocutor={user}
                    onJoin={this.handleJoin}
                    onHangup={() => this.handleHangupUser(user)}
                    conferenceId={id}
                    confToolbar
                    ellipsis
                  />
                </div>
              )
          )}
        {users.length > 0 && ownerIn && (
          <div className="card-body p-1 text-center small p-1">
            <span onClick={this.handleAddMemberClick} className="mx-1">
              <Icon name="person-add-2" color="var(--yn-blue-dark)">
                {formatMessage(messages.addMember)}
              </Icon>
            </span>
            {record && (
              <span onClick={this.handleRec} className="mx-1">
                <Icon name="record-button" color="var(--yn-blue-dark)">
                  {formatMessage(messages.startRec)}
                </Icon>
              </span>
            )}
            {recording && (
              <span onClick={this.handleStopRec} className="mx-1">
                <Icon name="record-button-filled" color="var(--yn-blue-dark)">
                  {formatMessage(messages.stopRecording)}
                </Icon>
              </span>
            )}
          </div>
        )}
        {users.length > 0 && ownerIn && this.state.addMemberPanelOpen && (
          <>
            <PhoneNumberSearchInput
              name="PhonePanelInput"
              id="PhonePanelInput"
              target="ConferencePanel"
              onEnter={() => this.handleAddMember(number)}
              error={this.state.error}
            />
            <PhoneNumberSearchList
              target="ConferencePanel"
              placement="right-start"
              onSelect={(num) => this.handleAddMember(num)}
            />
          </>
        )}
        <div className="card-body p-1">
          <Button
            text={formatMessage(messages.closeConferenceButton)}
            className="bt btn-danger btn-block"
            onClick={this.handleClose}
          />
        </div>
      </Fade>
    );
  }
}

function mapStateToProps(state) {
  return {
    users: getConferenceUsers(state),
    meId: getMeId(state),
    id: getMeConferenceId(state),
    number: getSearchedNumber(state, 'ConferencePanel'),
    activeCall: !!getMeActiveCallId(state),
    webrtcActive: isWebrtcActive(state),
    isRecordCallAvailable: isRecordCallAvailable(state),
    canRecord: hasGrants(state, 'PBX_REC'),
    hasRecordedCurrentCall: hasRecordedCurrentCall(state),
    recording: isRecording(state),
  };
}

export default injectIntl(
  connect(mapStateToProps, {
    fetch: fetchConferenceRequest,
    fetchMember: fetchMemberDataRequest,
    addMember: startConferenceRequest,
    join: joinConferenceRequest,
    hangupUser: hangupUserConferenceRequest,
    hangupConference: endConferenceRequest,
    hangupCall: hangupCallRequest,
    call: execCallRequest,
    addMemberWebrtc: addConferenceMemberWebrtcRequest,
    recordCall: recordCallRequest,
    stopRecordCall: stopRecordCallRequest,
    confirm: showConfirmModal,
    saveConference: saveConferenceRequest,
  })(ConferencePanel)
);
